import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';

import { FtueState } from './types';
import { TourStep, ViewedTour } from 'types';

type FtueTourResponse = {
  id?: number;
  name: string;
  steps?: TourStep[];
  tourSteps?: TourStep[];
};

const stepMatch = stepA => stepB =>
  (stepA.name && stepB.name && stepB.name === stepA.name) ||
  (stepA.id !== undefined && (stepB.tourStepId === stepA.id || stepB.id === stepA.id)) ||
  (stepA.tourStepId !== undefined &&
    (stepB.tourStepId === stepA.tourStepId || stepB.id === stepA.tourStepId));

const shouldViewStep = (toursViewed, tour) => step => {
  const viewedTour = toursViewed[tour.name] || (tour.id !== undefined && toursViewed[tour.id]);
  if (isEmpty(viewedTour)) return true;

  const viewedSteps = viewedTour.steps || viewedTour.tourStepSettings || [];
  const foundStep = viewedSteps.find(stepMatch(step));

  return !foundStep || !foundStep.viewed;
};

export const filterViewedSteps =
  (toursViewed: ViewedTour) =>
  (tours: FtueTourResponse[]): FtueTourResponse[] => {
    if (isEmpty(toursViewed)) return tours;

    return tours
      .map(tour => {
        const { tourSteps, ...restTour } = tour as any;
        const rawSteps = tour.steps || tourSteps || [];
        return {
          ...restTour,
          steps: rawSteps.filter(shouldViewStep(toursViewed, tour))
        };
      })
      .filter(tour => !isEmpty(tour.steps));
  };

export const updateWithViewedStep = (state: FtueState, viewedStep: TourStep) => {
  const { toursViewed, queuedTours, allTours } = state;
  const currentName = (viewedStep as any)?.tourName || queuedTours[0];
  const currentTour = allTours ? allTours[currentName] : undefined;

  const tourKey = currentTour && currentTour.id !== undefined ? currentTour.id : currentName;

  const existingKey =
    Object.keys(toursViewed).find(
      k => k === currentName || (currentTour && String(k) === String(currentTour.id))
    ) || tourKey;

  const existingTour = toursViewed[existingKey];

  if (!existingTour) {
    const stepId = viewedStep.id ?? viewedStep.tourStepId;
    const newEntry =
      currentTour && currentTour.id !== undefined
        ? {
            tourId: currentTour.id,
            name: currentName,
            tourStepSettings: [{ tourStepId: stepId, viewed: true }],
            steps: [viewedStep]
          }
        : {
            name: currentName,
            steps: [viewedStep]
          };

    return {
      ...toursViewed,
      [existingKey]: newEntry
    };
  }

  const updatedTour = cloneDeep(existingTour);
  const stepId = viewedStep.id ?? viewedStep.tourStepId;

  if (updatedTour.steps) {
    const existingStepIndex = updatedTour.steps.findIndex(stepMatch(viewedStep));
    if (existingStepIndex >= 0) {
      updatedTour.steps[existingStepIndex] = {
        ...updatedTour.steps[existingStepIndex],
        ...viewedStep,
        viewed: true
      };
    } else {
      updatedTour.steps.push({ ...viewedStep, viewed: true });
    }
  }

  if (updatedTour.tourStepSettings) {
    const existingSettingIndex = updatedTour.tourStepSettings.findIndex(
      s =>
        (stepId !== undefined && (s.tourStepId === stepId || s.id === stepId)) ||
        (viewedStep.name && s.name === viewedStep.name)
    );
    if (existingSettingIndex >= 0) {
      updatedTour.tourStepSettings[existingSettingIndex] = {
        ...updatedTour.tourStepSettings[existingSettingIndex],
        viewed: true
      };
    } else {
      updatedTour.tourStepSettings.push({
        tourStepId: stepId,
        viewed: true
      });
    }
  }

  if (!updatedTour.steps && !updatedTour.tourStepSettings) {
    updatedTour.steps = [{ ...viewedStep, viewed: true }];
  }

  return {
    ...toursViewed,
    [existingKey]: updatedTour
  };
};

const getUnviewedStepsFor =
  (name, tourObj, toursViewed) =>
  (needViewed, step): number => {
    const viewedTour =
      toursViewed[name] || (tourObj && tourObj.id !== undefined && toursViewed[tourObj.id]);

    if (!viewedTour) return needViewed + 1;

    const viewedSteps = viewedTour.steps || viewedTour.tourStepSettings || [];
    const foundStep = viewedSteps.find(stepMatch(step));

    if (!foundStep || !foundStep.viewed) {
      return needViewed + 1;
    }

    return needViewed;
  };

export function processHasUnviewedSteps(
  name: string,
  { allTours, toursViewed }: FtueState
): boolean {
  if (isEmpty(allTours) || !allTours?.[name]) return false;

  const tourObj = allTours[name];
  const viewedTour =
    toursViewed[name] || (tourObj && tourObj.id !== undefined && toursViewed[tourObj.id]);

  if (isEmpty(toursViewed) || !viewedTour) return true;

  const steps = tourObj.steps || [];
  const unviewedSteps = steps.reduce(getUnviewedStepsFor(name, tourObj, toursViewed), 0);

  return unviewedSteps > 0;
}

export function getFirstUnviewedStepIndex(
  name: string,
  { allTours, toursViewed }: FtueState
): number {
  const tour = allTours?.[name];
  if (!tour) return 0;

  const viewedTour = toursViewed[name] || (tour.id !== undefined && toursViewed[tour.id]);
  const viewedSteps = viewedTour?.steps || viewedTour?.tourStepSettings || [];
  const steps = tour.steps || [];
  const firstUnviewedIndex = steps.findIndex(step => {
    const viewedStep = viewedSteps.find(stepMatch(step));
    return !viewedStep || !viewedStep.viewed;
  });

  return firstUnviewedIndex === -1 ? 0 : firstUnviewedIndex;
}

export function removeFromTourQueue({ queuedTours }: FtueState, toRemove: string[] = []) {
  return queuedTours.reduce((queued, currTour) => {
    if (toRemove.includes(currTour)) return queued;

    (queued as any).push(currTour);

    return queued;
  }, []);
}
